import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import ListingCard from '../components/ListingCard';
import Filters from '../components/Filters';
import { fetchListings, fetchCategories } from '../lib/api';
import { seedCategories, seedListings } from '../data/seed';
import type { Category, Listing } from '../types';

export default function Browse() {
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get('category') || '';
  const searchParam = searchParams.get('search') || '';

  const [categories, setCategories] = useState<Category[]>(seedCategories);
  const [allListings, setAllListings] = useState<Listing[]>(seedListings);
  const [loading, setLoading] = useState(true);

  const [selectedCategory, setSelectedCategory] = useState(categoryParam);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedSort, setSelectedSort] = useState('rating');

  // Fetch all listings once - filter client-side for instant category switching
  useEffect(() => {
    fetchCategories().then(setCategories);
    fetchListings().then((data) => {
      setAllListings(data);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    setSelectedCategory(categoryParam);
  }, [categoryParam]);

  // Client-side filtering - instant, no server round-trip
  const listings = allListings.filter((l) => {
    if (selectedCategory && l.category_id !== selectedCategory) return false;
    if (selectedRegion && l.region_name !== selectedRegion) return false;
    if (searchParam) {
      const q = searchParam.toLowerCase().trim();
      // Normalize: remove spaces and hyphens so 'jetski' matches 'jet ski' or 'jet-ski'
      const qNorm = q.replace(/[\s-]+/g, '');
      const haystack = `${l.title} ${l.category_name || ''} ${l.region_name || ''} ${l.description} ${l.services.join(' ')}`.toLowerCase();
      const haystackNorm = haystack.replace(/[\s-]+/g, '');
      if (!haystack.includes(q) && !haystackNorm.includes(qNorm)) return false;
    }
    return true;
  }).sort((a, b) => {
    if (selectedSort === 'name') return a.title.localeCompare(b.title);
    if (selectedSort === 'reviews') return b.reviews_count - a.reviews_count;
    return b.rating - a.rating;
  });

  const regions = [...new Set(allListings.map(l => l.region_name).filter(Boolean))] as string[];

  const cat = categories.find(c => c.id === selectedCategory);
  const title = cat ? cat.name
    : searchParam ? `Search: "${searchParam}"`
    : 'All listings';
  const subtitle = cat ? `Browse ${cat.name} across New Zealand.`
    : searchParam ? 'Results matching your search term.'
    : 'Browse marine businesses across New Zealand. Filter by category, region, or search by name.';

  return (
    <div>
      <div className="max-w-[1440px] mx-auto px-4 md:px-8 pt-10 md:pt-16">
        <div className="flex items-center gap-2 text-sm text-text-muted mb-4">
          <Link to="/" className="text-text-secondary hover:text-accent transition-colors">Home</Link>
          <span>/</span>
          <span>{cat ? cat.name : searchParam ? 'Search results' : 'Browse'}</span>
        </div>
        <h1 className="font-display text-xl md:text-[2.5rem] font-bold tracking-tight mb-3">{title}</h1>
        <p className="text-text-secondary max-w-[600px] mb-6 text-sm md:text-base">{subtitle}</p>
      </div>

      <Filters
        categories={categories}
        regions={regions}
        selectedCategory={selectedCategory}
        selectedRegion={selectedRegion}
        selectedSort={selectedSort}
        onCategoryChange={setSelectedCategory}
        onRegionChange={setSelectedRegion}
        onSortChange={setSelectedSort}
        resultsCount={listings.length}
      />

      <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-4">
        {loading ? (
          <div className="text-center py-12 text-text-muted">Loading...</div>
        ) : listings.length === 0 ? (
          <div className="text-center py-12 md:py-24 text-text-muted">
            <h3 className="text-lg text-text-secondary mb-3">No listings found</h3>
            <p>Try adjusting your filters or search term.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-4 md:gap-5">
            {listings.map((l) => <ListingCard key={l.id} listing={l} />)}
          </div>
        )}
      </div>
    </div>
  );
}
